import { useEffect, useState } from "react";
import { api } from "../api";
import { getCurrentUser } from "../api";
import type { AppUser, PortalSettings } from "../types";
import { Users, Palette, Save, RotateCcw, Trash2, Plus, Eye, EyeOff } from "lucide-react";

const DEFAULTS: PortalSettings = {
  brand_bg: "#ffffff",
  brand_primary: "#5f1b2c",
  brand_secondary: "#e2c4b6",
  logo_url: "",
};

export function SettingsPage() {
  const user = getCurrentUser();
  const [tab, setTab] = useState<"users" | "portal">("users");

  if (user?.role !== "Owner") {
    return (
      <div className="text-center py-20 text-gray-400">
        <p className="text-lg">Access Denied</p>
        <p className="text-sm mt-1">Only Owner accounts can access Settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
      <div className="flex gap-2">
        <button onClick={() => setTab("users")} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "users" ? "bg-[#5f1b2c] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}><Users className="w-4 h-4" />User Management</button>
        <button onClick={() => setTab("portal")} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === "portal" ? "bg-[#5f1b2c] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}><Palette className="w-4 h-4" />Portal Settings</button>
      </div>
      {tab === "users" ? <UserManagement /> : <PortalSettingsPanel />}
    </div>
  );
}

// ─── User Management ───────────────────────────────────────────────────
function UserManagement() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<AppUser | null>(null);
  const [form, setForm] = useState({ name: "", email: "", role: "Coach", password: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const loadUsers = async () => {
    try {
      const res = await api.getUsers();
      setUsers(res.users);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadUsers(); }, []);

  const openAdd = () => {
    setEditingUser(null);
    setForm({ name: "", email: "", role: "Coach", password: "" });
    setShowForm(true);
    setError("");
  };

  const openEdit = (u: AppUser) => {
    setEditingUser(u);
    setForm({ name: u.name, email: u.email, role: u.role, password: "" });
    setShowForm(true);
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editingUser) {
        const data: any = { name: form.name, email: form.email, role: form.role };
        if (form.password) data.password = form.password;
        await api.updateUser(editingUser.id, data);
      } else {
        await api.createUser({ name: form.name, email: form.email, role: form.role, password: form.password || undefined });
      }
      setShowForm(false);
      loadUsers();
    } catch (err: any) {
      setError(err.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    if (!confirm("Deactivate this user? They will no longer be able to log in.")) return;
    try {
      await api.deactivateUser(id);
      loadUsers();
    } catch (e: any) {
      alert(e.message || "Failed to deactivate");
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Admin Users</h2>
        <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2 bg-[#5f1b2c] text-white rounded-lg text-sm font-medium hover:bg-[#4a1520] transition-colors"><Plus className="w-4 h-4" />Add User</button>
      </div>
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 bg-gray-50 rounded-lg animate-pulse" />)}</div>
      ) : (
        <div className="divide-y divide-gray-100">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 text-sm">{u.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${u.role === "Owner" ? "bg-[#5f1b2c] text-white" : "bg-gray-100 text-gray-600"}`}>{u.role}</span>
                  {!u.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-red-100 text-red-600">Deactivated</span>}
                </div>
                <span className="text-xs text-gray-500">{u.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => openEdit(u)} className="text-xs text-gray-500 hover:text-[#5f1b2c] px-2 py-1 rounded hover:bg-gray-50">Edit</button>
                {u.active && (
                  <button onClick={() => handleDeactivate(u.id)} className="text-xs text-gray-400 hover:text-red-500 px-2 py-1 rounded hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{editingUser ? "Edit User" : "Add New User"}</h3>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input placeholder="Full Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5f1b2c]" />
              <input placeholder="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5f1b2c]" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5f1b2c]">
                <option value="Coach">Coach</option>
                <option value="Owner">Owner</option>
              </select>
              <div className="relative">
                <input placeholder={editingUser ? "New password (leave blank to keep)" : "Password *"} required={!editingUser} type={showPassword ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 pr-10 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5f1b2c]" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
              {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-[#5f1b2c] text-white rounded-lg text-sm font-medium hover:bg-[#4a1520] disabled:opacity-50">{saving ? "Saving..." : "Save"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Portal Settings ───────────────────────────────────────────────────
function PortalSettingsPanel() {
  const [settings, setSettings] = useState<PortalSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.getPortalSettings().then((r) => {
      setSettings({ ...DEFAULTS, ...r.settings });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const update = (key: keyof PortalSettings, value: string) => {
    setSettings({ ...settings, [key]: value });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updatePortalSettingsBulk(settings as unknown as Record<string, string>);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      alert(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm("Reset to default colours and logo?")) return;
    setSettings(DEFAULTS);
    setSaving(true);
    try {
      await api.updatePortalSettingsBulk(DEFAULTS as unknown as Record<string, string>);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      alert(e.message || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="space-y-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 bg-gray-50 rounded-lg animate-pulse" />)}</div>;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">Appearance</h2>

      {/* Logo */}
      <div>
        <label className="text-sm text-gray-600 block mb-1">Logo URL</label>
        <input type="url" value={settings.logo_url} onChange={(e) => update("logo_url", e.target.value)} placeholder="https://..." className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5f1b2c]" />
        {settings.logo_url && (
          <div className="mt-2 flex items-center gap-3">
            <img src={settings.logo_url} alt="Logo preview" className="h-10 w-10 rounded-lg object-cover border border-gray-200" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
            <span className="text-xs text-gray-400">Preview</span>
          </div>
        )}
      </div>

      {/* Brand colours */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {([
          { key: "brand_bg" as const, label: "Background" },
          { key: "brand_primary" as const, label: "Primary Accent" },
          { key: "brand_secondary" as const, label: "Secondary Accent" },
        ]).map(({ key, label }) => (
          <div key={key}>
            <label className="text-sm text-gray-600 block mb-1">{label}</label>
            <div className="flex items-center gap-2">
              <input type="color" value={settings[key]} onChange={(e) => update(key, e.target.value)} className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
              <input type="text" value={settings[key]} onChange={(e) => update(key, e.target.value)} className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-[#5f1b2c]" />
            </div>
          </div>
        ))}
      </div>

      {/* Preview */}
      <div>
        <label className="text-sm text-gray-600 block mb-1">Preview</label>
        <div className="rounded-xl p-4 border border-gray-200" style={{ background: settings.brand_bg }}>
          <div className="flex items-center gap-3">
            {settings.logo_url && <img src={settings.logo_url} alt="" className="w-8 h-8 rounded-lg object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />}
            <span className="font-bold" style={{ color: settings.brand_primary }}>FGNKD</span>
            <span className="text-sm px-2 py-0.5 rounded-full" style={{ background: settings.brand_secondary, color: settings.brand_primary }}>Sample Badge</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[#5f1b2c] text-white rounded-lg text-sm font-medium hover:bg-[#4a1520] disabled:opacity-50 transition-colors"><Save className="w-4 h-4" />{saving ? "Saving..." : saved ? "Saved!" : "Save Changes"}</button>
        <button onClick={handleReset} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors"><RotateCcw className="w-4 h-4" />Reset to Defaults</button>
      </div>
    </div>
  );
}